import React, { useState, useRef, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import {
  Box, Typography, IconButton, TextField, CircularProgress,
  Tooltip, Paper, Divider, Chip, Fade,
} from '@mui/material';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

const GOLD   = '#C9A84C';
const NAVY   = '#0A2342';
const WIDGET = 380;

const QUICK_QUESTIONS = [
  'Is this firm compliant right now?',
  'Which brokers need CE attention?',
  'What do I need for a regulatory audit?',
  'Explain this compliance gap',
  'What licences are expiring soon?',
];

function Message({ msg }) {
  const [copied, setCopied] = useState(false);
  const isAI = msg.role === 'assistant';

  function copyText() {
    navigator.clipboard.writeText(msg.text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: isAI ? 'flex-start' : 'flex-end',
        mb: 1.5,
      }}
    >
      <Box
        sx={{
          maxWidth: '88%',
          px: 1.5,
          py: 1,
          borderRadius: isAI ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
          bgcolor: isAI ? '#f0f4f8' : NAVY,
          color: isAI ? '#1a1a1a' : '#fff',
          fontSize: 13,
          lineHeight: 1.55,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {msg.text}
      </Box>
      {isAI && (
        <Tooltip title={copied ? 'Copied!' : 'Copy response'}>
          <IconButton size="small" onClick={copyText} sx={{ mt: 0.25, p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}>
            {copied ? <CheckIcon sx={{ fontSize: 13 }} /> : <ContentCopyIcon sx={{ fontSize: 13 }} />}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

export default function AIAssistant() {
  const { user } = useContext(AuthContext);
  const [open, setOpen]       = useState(false);
  const [messages, setMessages] = useState([
    { role: 'assistant', text: "Hi! I'm Authra Advisor, your AI-powered compliance assistant. I can answer questions about licensing, CE requirements, E&O, regulatory audits, and more. What would you like to know?" },
  ]);
  const [input, setInput]     = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef             = useRef(null);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  async function sendMessage(text) {
    const question = (text || input).trim();
    if (!question || loading) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setLoading(true);
    try {
      const payload = { question };
      if (user?.firmId) payload.firmId = user.firmId;
      if (user?.id)     payload.userId = user.id;
      const { data } = await api.post('/ai/query', payload);
      setMessages(prev => [...prev, { role: 'assistant', text: data.answer || 'No response received.' }]);
    } catch (err) {
      const msg = err.response?.data?.error || 'Sorry, the AI service is temporarily unavailable.';
      setMessages(prev => [...prev, { role: 'assistant', text: msg }]);
    } finally {
      setLoading(false);
    }
  }

  function handleKey(e) {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  }

  return (
    <>
      {/* Floating bubble */}
      <Tooltip title="Authra Advisor" placement="left">
        <Box
          onClick={() => setOpen(v => !v)}
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 52,
            height: 52,
            borderRadius: '50%',
            bgcolor: GOLD,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            boxShadow: '0 4px 20px rgba(201,168,76,0.45)',
            zIndex: 1400,
            transition: 'transform 0.2s, box-shadow 0.2s',
            '&:hover': { transform: 'scale(1.08)', boxShadow: '0 6px 28px rgba(201,168,76,0.6)' },
          }}
        >
          {open
            ? <CloseIcon sx={{ color: '#fff', fontSize: 22 }} />
            : <SmartToyIcon sx={{ color: '#fff', fontSize: 24 }} />
          }
        </Box>
      </Tooltip>

      {/* Chat drawer */}
      <Fade in={open}>
        <Paper
          elevation={8}
          sx={{
            position: 'fixed',
            bottom: 88,
            right: 24,
            width: WIDGET,
            maxWidth: 'calc(100vw - 48px)',
            height: 520,
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            zIndex: 1399,
            border: `1px solid ${GOLD}30`,
          }}
        >
          {/* Header */}
          <Box sx={{ px: 2, py: 1.5, bgcolor: NAVY, display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesomeIcon sx={{ color: GOLD, fontSize: 18 }} />
            <Box flex={1}>
              <Typography variant="body2" fontWeight={700} color="#fff" lineHeight={1.2}>
                Authra Advisor
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.6)', fontSize: 10 }}>
                Powered by Claude
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)', p: 0.5 }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Messages */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, py: 1.5, bgcolor: '#fafbfc' }}>
            {messages.map((m, i) => <Message key={i} msg={m} />)}
            {loading && (
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CircularProgress size={14} sx={{ color: GOLD }} />
                <Typography variant="caption" color="text.secondary">Thinking…</Typography>
              </Box>
            )}
            <div ref={bottomRef} />
          </Box>

          {/* Quick questions */}
          <Box sx={{ px: 1.5, py: 1, borderTop: '1px solid #e8eef4', bgcolor: '#fff' }}>
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              {QUICK_QUESTIONS.map(q => (
                <Chip
                  key={q}
                  label={q}
                  size="small"
                  onClick={() => sendMessage(q)}
                  disabled={loading}
                  sx={{
                    fontSize: 10,
                    height: 22,
                    cursor: 'pointer',
                    bgcolor: '#f0f4f8',
                    '&:hover': { bgcolor: `${GOLD}22` },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Divider />

          {/* Input */}
          <Box sx={{ px: 1.5, py: 1, display: 'flex', alignItems: 'flex-end', gap: 1, bgcolor: '#fff' }}>
            <TextField
              multiline
              maxRows={3}
              fullWidth
              size="small"
              placeholder="Ask Authra Advisor about compliance, CE, licences…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              disabled={loading}
              sx={{
                '& .MuiOutlinedInput-root': { borderRadius: 2, fontSize: 13 },
              }}
            />
            <IconButton
              size="small"
              onClick={() => sendMessage()}
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: GOLD,
                color: '#fff',
                borderRadius: 1.5,
                p: 1,
                '&:hover': { bgcolor: '#b8913f' },
                '&:disabled': { bgcolor: '#e0e0e0', color: '#9e9e9e' },
              }}
            >
              <SendIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              textAlign: 'center',
              color: '#5B6B7F',
              fontSize: '10px',
              px: 1,
              pb: 0.5,
              lineHeight: 1.3,
              bgcolor: '#fff',
            }}
          >
            Authra Advisor provides general compliance information only. Not legal or regulatory advice. Responses use live Authra data scoped to your account.
          </Typography>
        </Paper>
      </Fade>
    </>
  );
}
